import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { ComputeFactory } from '../../models/ComputeFactory'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { API_BASE } from '../../utils/constants'
import { isForVendor } from '../../utils/records'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps } from '../PocketComponent'
import { usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { InvoiceView } from './InvoiceView'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (!urlParams.id) {
        throw new RecordNotFoundError('Invoice not found')
    }

    await RecordFactory.getInstance().fetchInvoice(urlParams.id)
    const invoice = await RecordFactory.getInstance().loadInvoice(urlParams.id)
    if (!invoice) {
        throw new RecordNotFoundError('Invoice not found')
    }

    const client = invoice.client
    if (!client) {
        throw new RecordNotFoundError('Client not found')
    }

    const payments = await ComputeFactory.getInstance().fetchInvoicePayments(invoice)

    return { invoice, client, payments }
}

const render = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { invoice, client, payments } = data

    const navigate = useNavigate()

    usePageTitle('Invoice')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
        secondaryButton?: {
            title: string
            onClick: () => void
        }
    } | null>(null)

    const handleResend = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().sendInvoice(invoice, false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
    }

    const handleSendReceipt = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().sendReceipt(invoice)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleUndo = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().rollbackTransaction(invoice)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleCancel = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().cancelInvoice(invoice, false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        if (invoice.type !== 'schedule') {
            buttons.push({ title: 'Edit', onClick: () => navigate(`/invoices/${invoice.id}/edit`) })
        }

        if (invoice.type !== 'schedule') {
            if (!invoice.paidDate && !invoice.cancelledDate && !!invoice.amount) {
                buttons.push({
                    title: 'Charge',
                    onClick: () => navigate(`/charge/invoice/${invoice.id}`),
                })
            }
            else if (!invoice.paidDate && !invoice.cancelledDate) {
                buttons.push({
                    title: 'Mark paid',
                    onClick: () => navigate(`/charge/invoice/${invoice.id}`),
                })
            }
        }

        if (invoice.type !== 'schedule') {
            buttons.push({ title: 'Copy', onClick: () => navigate(`/invoices/${invoice.id}/copy`) })
        }

        buttons.push({
            title: 'Print',
            onClick: () => window.open(`${API_BASE}/invoice/${invoice.id}/print`, '_blank'),
        })

        if (invoice.type !== 'schedule') {
            buttons.push({
                title: 'Resend',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Resend invoice?',
                        confirmationButtonText: 'Send invoice',
                        body: 'Would you like to resend this invoice?',
                        onConfirm: handleResend,
                    }),
            })
        }

        if (invoice.type !== 'schedule' && invoice.markPaidDate) {
            buttons.push({
                title: 'Receipt',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Send receipt',
                        confirmationButtonText: 'Send receipt',
                        body: 'Would you like to send the client a receipt?',
                        onConfirm: handleSendReceipt,
                    }),
            })
        }

        if (invoice.type !== 'schedule' && invoice.cancelledDate && isForVendor(invoice, owner)) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Undo mark paid',
                        confirmationButtonText: 'Undo mark paid',
                        body: 'Would you like to undo this payment?',
                        onConfirm: handleUndo,
                    }),
            })
        }
        else if (
            invoice.type !== 'schedule' &&
            invoice.markPaidDate &&
            isForVendor(invoice, owner)
        ) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Undo mark paid',
                        confirmationButtonText: 'Undo mark paid',
                        body: 'Would you like to undo this payment?',
                        onConfirm: handleUndo,
                    }),
            })
        }

        if (!invoice.cancelledDate && !invoice.paidDate) {
            buttons.push({
                title: 'Cancel',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Cancel invoice?',
                        confirmationButtonText: 'Cancel invoice',
                        body: 'This action cannot be undone.',
                        onConfirm: handleCancel,
                    }),
            })
        }

        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^((\/invoices.*\/(new|edit|copy))|(\/charge\/invoice\/.*))/,
    })

    return (
        <BaseRecordView
            title={invoice.type === 'schedule' ? 'Scheduled invoice' : 'Invoice'}
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Invoices',
                          onClick: () => navigate('/dashboards/invoices'),
                      }
            }
            headerButtons={headerButtons}
            tag={invoice.statusTag ?? undefined}
        >
            <InvoiceView
                invoice={invoice}
                isWorkflowReviewStep={false}
                client={client}
                payments={payments}
            />

            {confirmationModalData !== null && (
                <Modal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    size="small"
                    title={confirmationModalData.title}
                    showDividers={false}
                    footer={
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {confirmationModalData.secondaryButton && (
                                <Button
                                    intent="tertiary_deprecated"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        confirmationModalData.secondaryButton?.onClick()
                                    }}
                                >
                                    {confirmationModalData.secondaryButton.title}
                                </Button>
                            )}
                            <Button
                                intent="primary"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    confirmationModalData.onConfirm()
                                }}
                            >
                                {confirmationModalData.confirmationButtonText}
                            </Button>
                        </div>
                    }
                >
                    <div className="text-gray-600">{confirmationModalData.body}</div>
                </Modal>
            )}
        </BaseRecordView>
    )
}

export const InvoicePage = usePSComponent(render, viewWillAppear)
